/*
 * Purpose: Persist AI-built + user connectors in AutoBlocks resource library
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { create } from 'zustand';
import { BUILTIN_CONNECTORS, getBuiltinConnector } from './builtins';
import { matchOutcomeSeed } from '@/seeds/averageOutcomes';
import type { AutoBlocksConnector } from './types';

const STORAGE_KEY = 'autoblocks_connector_library';

function loadCustom(): AutoBlocksConnector[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as AutoBlocksConnector[];
  } catch {
    return [];
  }
}

function persistCustom(custom: AutoBlocksConnector[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(custom));
}

interface ConnectorLibraryStore {
  customConnectors: AutoBlocksConnector[];
  load: () => void;
  getAll: () => AutoBlocksConnector[];
  getById: (id: string) => AutoBlocksConnector | undefined;
  register: (connector: AutoBlocksConnector) => void;
  remove: (id: string) => void;
}

export const useConnectorLibraryStore = create<ConnectorLibraryStore>((set, get) => ({
  customConnectors: loadCustom(),

  load: () => set({ customConnectors: loadCustom() }),

  getAll: () => [...BUILTIN_CONNECTORS, ...get().customConnectors],

  getById: (id) => getBuiltinConnector(id) ?? get().customConnectors.find((c) => c.id === id),

  register: (connector) => {
    set((state) => {
      const filtered = state.customConnectors.filter((c) => c.id !== connector.id);
      const customConnectors = [...filtered, connector];
      persistCustom(customConnectors);
      return { customConnectors };
    });
  },

  remove: (id) => {
    if (getBuiltinConnector(id)) return;
    set((state) => {
      const customConnectors = state.customConnectors.filter((c) => c.id !== id);
      persistCustom(customConnectors);
      return { customConnectors };
    });
  },
}));

export function listConnectorCatalogForPrompt(): string {
  return useConnectorLibraryStore
    .getState()
    .getAll()
    .map((c) => `${c.id} (${c.category}): ${c.description} [${c.capabilities.map((x) => x.id).join(', ')}]`)
    .join('\n');
}

export function findConnectorsForOutcome(text: string): AutoBlocksConnector[] {
  const seed = matchOutcomeSeed(text);
  if (seed) {
    return seed.expectedConnectorIds
      .map((id) => getBuiltinConnector(id))
      .filter((c): c is AutoBlocksConnector => Boolean(c));
  }

  const lower = text.toLowerCase();
  return useConnectorLibraryStore.getState().getAll().filter((c) => {
    const hay = `${c.name} ${c.category} ${c.description} ${c.capabilities.map((x) => x.label).join(' ')}`.toLowerCase();
    if (lower.includes('email') || lower.includes('gmail')) return c.id === 'ab.gmail' || hay.includes('email');
    if (lower.includes('slack')) return hay.includes('slack') || c.category === 'integration';
    if (/resume|cover|ai|gpt|openai/i.test(lower)) return c.id === 'ab.openai' || c.category === 'ai';
    if (/track|store|log|spreadsheet/i.test(lower)) return c.id === 'ab.storage';
    if (/notify|alert|remind/i.test(lower)) return c.id === 'ab.notify' || c.id === 'ab.scheduler';
    return false;
  });
}
